import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Header from './components/Header17';
import Footer from './components/Footer17';

const Template17Shop = () => {
    const navigate = useNavigate();
    const [activeFilter, setActiveFilter] = useState('All');
    const [sortBy, setSortBy] = useState('featured');
    const [currentPage, setCurrentPage] = useState(1);
    const [hoveredCard, setHoveredCard] = useState(null);

    const categories = ['All', 'Curated Sarees', 'Art Kurtas', 'Gallery Pieces', 'Designer Edits'];

    const sortOptions = [
        { value: 'featured', label: 'Featured' },
        { value: 'price-low', label: 'Price: Low to High' },
        { value: 'price-high', label: 'Price: High to Low' },
        { value: 'newest', label: 'Newest First' },
    ];

    const products = [
        { id: 1, name: 'Chanderi Silk Saree', category: 'Curated Sarees', price: 7999, image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 2, name: 'Banarasi Art Weave', category: 'Curated Sarees', price: 12499, image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Block Print Kurta', category: 'Art Kurtas', price: 4999, image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 4, name: 'Tussar Silk Dupatta', category: 'Gallery Pieces', price: 3499, image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Ikat Palazzo Set', category: 'Designer Edits', price: 6999, image: 'https://images.pexels.com/photos/6712360/pexels-photo-6712360.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 6, name: 'Kalamkari Anarkali', category: 'Gallery Pieces', price: 8499, image: 'https://images.pexels.com/photos/7275385/pexels-photo-7275385.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 7, name: 'Ajrakh Saree', category: 'Curated Sarees', price: 19999, image: 'https://images.pexels.com/photos/8953474/pexels-photo-8953474.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 8, name: 'Maheshwari Silk Set', category: 'Designer Edits', price: 11999, image: 'https://images.pexels.com/photos/2955375/pexels-photo-2955375.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    const filteredProducts = activeFilter === 'All'
        ? products
        : products.filter(p => p.category === activeFilter);

    const sortedProducts = [...filteredProducts].sort((a, b) => {
        if (sortBy === 'price-low') return a.price - b.price;
        if (sortBy === 'price-high') return b.price - a.price;
        return 0;
    });

    const totalPages = 3;

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Source+Serif+4:wght@400;600;700&family=Inter:wght@300;400;500&display=swap');
            `}</style>

            <div style={{ fontFamily: "'Inter', -apple-system, sans-serif" }} className="min-h-screen bg-white">
                <Header />

                <main className="pt-[100px] md:pt-[120px] pb-16">
                    {/* Page Header */}
                    <div className="text-center py-12 lg:py-20" style={{ borderBottom: '1px solid #E4E4E7' }}>
                        <p className="text-xs tracking-[0.4em] uppercase mb-4" style={{ color: '#71717A', fontWeight: 300 }}>
                            Browse the Collection
                        </p>
                        <h1
                            className="text-3xl lg:text-5xl font-normal"
                            style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: '#18181B' }}
                        >
                            The Gallery
                        </h1>
                    </div>

                    <div className="max-w-[1400px] mx-auto px-6 lg:px-8 mt-10">
                        {/* Filters */}
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-10">
                            <div className="flex flex-wrap gap-3">
                                {categories.map((cat) => (
                                    <button
                                        key={cat}
                                        onClick={() => setActiveFilter(cat)}
                                        style={{
                                            fontFamily: "'Inter', sans-serif",
                                            fontSize: '13px',
                                            fontWeight: activeFilter === cat ? 500 : 300,
                                            color: activeFilter === cat ? '#FFFFFF' : '#18181B',
                                            backgroundColor: activeFilter === cat ? '#18181B' : 'transparent',
                                            border: '1px solid #18181B',
                                            padding: '8px 20px',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            letterSpacing: '0.5px',
                                        }}
                                    >
                                        {cat}
                                    </button>
                                ))}
                            </div>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                style={{
                                    fontFamily: "'Inter', sans-serif",
                                    fontSize: '13px',
                                    fontWeight: 300,
                                    color: '#18181B',
                                    backgroundColor: '#FFFFFF',
                                    border: '1px solid #E4E4E7',
                                    padding: '10px 16px',
                                    outline: 'none',
                                    cursor: 'pointer',
                                }}
                            >
                                {sortOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Product Grid */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8">
                            {sortedProducts.map((product) => (
                                <div
                                    key={product.id}
                                    onClick={() => navigate(`/template17/product/${product.id}`)}
                                    onMouseEnter={() => setHoveredCard(product.id)}
                                    onMouseLeave={() => setHoveredCard(null)}
                                    className="cursor-pointer group"
                                >
                                    <div className="aspect-[3/4] overflow-hidden mb-3 lg:mb-4" style={{ backgroundColor: '#FAFAFA' }}>
                                        <img
                                            src={product.image}
                                            alt={product.name}
                                            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                                        />
                                    </div>
                                    <h3
                                        className="text-sm lg:text-[15px] font-normal mb-1"
                                        style={{
                                            fontFamily: "'Source Serif 4', Georgia, serif",
                                            color: '#18181B',
                                            textDecoration: hoveredCard === product.id ? 'underline' : 'none',
                                            textUnderlineOffset: '3px',
                                        }}
                                    >
                                        {product.name}
                                    </h3>
                                    <p className="text-sm" style={{ color: '#18181B', fontWeight: 400 }}>
                                        {`\u20B9${product.price.toLocaleString('en-IN')}`}
                                    </p>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        <div className="flex justify-center items-center gap-4 mt-16">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    border: '1px solid #E4E4E7',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'transparent',
                                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                    opacity: currentPage === 1 ? 0.3 : 1,
                                }}
                            >
                                <ChevronLeft size={18} color="#18181B" />
                            </button>
                            {[...Array(totalPages)].map((_, i) => (
                                <button
                                    key={i + 1}
                                    onClick={() => setCurrentPage(i + 1)}
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        border: '1px solid #E4E4E7',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: currentPage === i + 1 ? '#18181B' : 'transparent',
                                        color: currentPage === i + 1 ? '#FFFFFF' : '#18181B',
                                        fontFamily: "'Inter', sans-serif",
                                        fontSize: '14px',
                                        fontWeight: 400,
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                    }}
                                >
                                    {i + 1}
                                </button>
                            ))}
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    border: '1px solid #E4E4E7',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'transparent',
                                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                    opacity: currentPage === totalPages ? 0.3 : 1,
                                }}
                            >
                                <ChevronRight size={18} color="#18181B" />
                            </button>
                        </div>
                    </div>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default Template17Shop;
